import { Title, Description, Close } from '@radix-ui/react-dialog';
import React, { useState } from 'react';

import { Button, AreaUnitsSelector, CurrencySelector, Dialog, Popover } from '$partials/ui';
import { AreaSlider, Checkbox, MultiSelectCloud, PriceSlider, Select } from '../individual';
import { PropertySortInput } from '$partials/listings';
import BaseSelect from '$partials/fields/select.jsx';
import IconChevron from '$partials/icons/icon-chevron';
import IconClose from '$partials/icons/icon-close';
import IconTune from '$partials/icons/icon-tune';
import LocationAutocompleteInput from '$partials/fields/location-autocomplete';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/filters/panels.d.ts').PropertyListingFilters}
 */
export const PropertyListingFilters = ({
    amenitiesFilter,
    areasFilter,
    bathroomsFilter,
    bedroomsFilter,
    dialogFilterSelection,
    mapBoundsFilter,
    officeFilter,
    outdoorFilter,
    pageTitle,
    priceFilter,
    promotedFilterSelection,
    setSortBy,
    sizeFilter,
    sortBy,
    tenuresFilter,
    typesFilter,
    newHomeFilter
}) => {
    const [areasOpen, setAreasOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [locationSearchOpen, setLocationSearchOpen] = useState(false);

    const clearFilters = () => {
        amenitiesFilter.clear();
        bathroomsFilter.clear();
        bedroomsFilter.clear();
        outdoorFilter.clear();
        priceFilter.clear();
        sizeFilter.clear();
        tenuresFilter.clear();
        typesFilter.clear();
        officeFilter.clear();
        newHomeFilter.clear();
    };

    return (
        <div className='layout-container layout-site-outer-padding py-16 md:py-24 w-full flex md:flex-wrap gap-8 theme-brand'>
            <div className='w-full flex gap-8 items-center'>
                <div className='w-full md:w-8/12 flex gap-8 items-center'>
                    {(mapBoundsFilter?.defaultValue?.centre && areasFilter.value?.length === 0) &&
                        <div className='w-full'>
                            <LocationAutocompleteInput
                                bounds={{
                                    nw: mapBoundsFilter?.defaultValue?.nw,
                                    se: mapBoundsFilter?.defaultValue?.se,
                                }}
                                onChange={(value) => mapBoundsFilter.setValue(value)}
                                onOpenChange={setLocationSearchOpen}
                                origin={mapBoundsFilter?.defaultValue?.centre}
                                placeholder='Search'
                                value={mapBoundsFilter.value} />
                        </div>
                    }

                    {(!locationSearchOpen && !mapBoundsFilter.value?.search && areasFilter.value?.length === 0) &&
                        <div className='type-style-eyebrow hidden md:block'>
                            Or
                        </div>
                    }

                    {/* Areas */}
                    {(!locationSearchOpen && !mapBoundsFilter.value?.search) &&
                        <div className='w-full'>
                            <Button
                                className='button-primary-filled type-style-eyebrow w-full !justify-between truncate'
                                onClick={() => setAreasOpen(true)}
                                renderIcon={<IconChevron />}>
                                {`Select areas${areasFilter.value?.length > 0 ? ` (${areasFilter.value.length})` : ''}`}
                            </Button>
                            <Dialog
                                className='filter-dialog theme-light'
                                onOpenChange={(open) => setAreasOpen(open)}
                                open={areasOpen}>
                                <header className='flex justify-between p-16 border-b border-main-border h-[59px]'>
                                    <Title>
                                        Select areas
                                    </Title>
                                    <Description className='sr-only'>
                                        Filter the listing by area
                                    </Description>
                                    <Close>
                                        <IconClose />
                                    </Close>
                                </header>
                                <div className='p-16'>
                                    <MultiSelectCloud
                                        name='areas'
                                        options={areasFilter.options}
                                        toggleValue={areasFilter.toggleValue}
                                        value={areasFilter.value} />
                                </div>
                                <footer className='sticky bottom-0 flex justify-between px-16 py-12 gap-8'>
                                    <Button
                                        className='button-brand-filled type-style-eyebrow w-full'
                                        onClick={() => setAreasOpen(false)}>
                                        {`Choose ${areasFilter.value?.length} area${areasFilter.value?.length !== 1 ? 's' : ''}`}
                                    </Button>
                                    <Button
                                        className='button-secondary-filled type-style-eyebrow'
                                        onClick={() => areasFilter.clear()}>
                                        Clear
                                    </Button>
                                </footer>
                            </Dialog>
                        </div>
                    }
                </div>

                <div className='hidden md:flex gap-32 w-full flex-1 justify-end'>
                    <AreaUnitsSelector className='!p-0 !h-[auto] !bg-[transparent]' />
                    <CurrencySelector
                        className='!p-0 !h-[auto] !bg-[transparent]'
                        pageTitle={pageTitle} />
                </div>
            </div>

            <div className='md:w-8/12 flex gap-8 justify-between'>
                <div className='flex gap-8'>
                    {/* Promoted filters */}
                    {(promotedFilterSelection?.length > 0) &&
                        <div className='hidden md:flex gap-8'>
                            {promotedFilterSelection?.map(({ __typename: filterType, ...filter }, i) => {
                                switch (filterType) {
                                    case 'AmenityFilterBlock':
                                        if (filter.amenity) {
                                            return (
                                                <Popover
                                                    contentClassName='w-[360px]'
                                                    key={i}
                                                    label={filter.amenity?.label}
                                                    triggerClassName='button-primary-filled type-style-eyebrow'>
                                                    <Checkbox
                                                        checked={amenitiesFilter.value?.includes(filter.amenity?.value)}
                                                        label={filter.amenity?.label}
                                                        name={`amenity-${filter.amenity?.value}`}
                                                        onCheckedChange={() => amenitiesFilter.toggleValue(filter.amenity?.value)} />
                                                </Popover>
                                            );
                                        }
                                        break;
                                    case 'AmenitiesFilterBlock':
                                        if (amenitiesFilter.options.length > 1) {
                                            return (
                                                <Popover
                                                    key={i}
                                                    label={`Amenities${amenitiesFilter.value?.length > 0 ? ` (${amenitiesFilter.value.length})` : ''}`}
                                                    triggerClassName='button-primary-filled type-style-eyebrow'>
                                                    <MultiSelectCloud
                                                        name='amenities'
                                                        options={amenitiesFilter.options}
                                                        toggleValue={amenitiesFilter.toggleValue}
                                                        value={amenitiesFilter.value} />
                                                </Popover>
                                            );
                                        }
                                        break;
                                    case 'BathroomsFilterBlock':
                                        if (bathroomsFilter.options.length > 2) {
                                            return (
                                                <BaseSelect
                                                    displayValue={`Beds${bathroomsFilter.value > 0 ? ` (${bathroomsFilter.options.find((opt) => String(opt.value) === String(bathroomsFilter.value))?.label})` : ''}`}
                                                    key={i}
                                                    name='bathrooms'
                                                    onChange={(v) => bathroomsFilter.setValue(v)}
                                                    options={bathroomsFilter.options}
                                                    triggerClassName='button-primary-filled type-style-eyebrow h-[46px]'
                                                    value={bathroomsFilter.value} />
                                            );
                                        }
                                        break;
                                    case 'BedroomsFilterBlock':
                                        if (bedroomsFilter.options.length > 2) {
                                            return (
                                                <BaseSelect
                                                    displayValue={`Beds${bedroomsFilter.value > 0 ? ` (${bedroomsFilter.options.find((opt) => String(opt.value) === String(bedroomsFilter.value))?.label})` : ''}`}
                                                    key={i}
                                                    name='bedrooms'
                                                    onChange={(v) => bedroomsFilter.setValue(v)}
                                                    options={bedroomsFilter.options}
                                                    triggerClassName='button-primary-filled type-style-eyebrow h-[46px]'
                                                    value={bedroomsFilter.value} />
                                            );
                                        }
                                        break;
                                    case 'OutdoorFilterBlock':
                                        return (
                                            <Popover
                                                contentClassName='w-[360px]'
                                                key={i}
                                                label='Outdoor space'
                                                triggerClassName='button-primary-filled type-style-eyebrow'>
                                                <Checkbox
                                                    checked={outdoorFilter.value}
                                                    label='Outdoor space'
                                                    name='outdoor'
                                                    onCheckedChange={outdoorFilter.setValue} />
                                            </Popover>
                                        );
                                    case 'PriceFilterBlock':
                                        if (priceFilter.range[1] > priceFilter.range[0]) {
                                            return (
                                                <Popover
                                                    contentClassName='w-[360px]'
                                                    key={i}
                                                    label='Price range'
                                                    triggerClassName='button-primary-filled type-style-eyebrow'>
                                                    <PriceSlider
                                                        max={priceFilter.range[1]}
                                                        min={priceFilter.range[0]}
                                                        name='price'
                                                        onChange={(value) => priceFilter.setValue(value)}
                                                        value={priceFilter.value} />
                                                </Popover>
                                            );
                                        }
                                        break;
                                    case 'SizeFilterBlock':
                                        if (sizeFilter.range[1] > sizeFilter.range[0]) {
                                            return (
                                                <Popover
                                                    contentClassName='w-[360px]'
                                                    key={i}
                                                    label='Size'
                                                    triggerClassName='button-primary-filled type-style-eyebrow'>
                                                    <AreaSlider
                                                        max={sizeFilter.range[1]}
                                                        min={sizeFilter.range[0]}
                                                        onChange={sizeFilter.setValue}
                                                        value={sizeFilter.value} />
                                                </Popover>
                                            );
                                        }
                                        break;
                                    case 'TenuresFilterBlock':
                                        if (tenuresFilter.options.length > 1) {
                                            return (
                                                <Popover
                                                    key={i}
                                                    label={`Property Tenures${tenuresFilter.value?.length > 0 ? ` (${tenuresFilter.value.length})` : ''}`}
                                                    triggerClassName='button-primary-filled type-style-eyebrow'>
                                                    <MultiSelectCloud
                                                        name='tenures'
                                                        options={tenuresFilter.options}
                                                        toggleValue={tenuresFilter.toggleValue}
                                                        value={tenuresFilter.value} />
                                                </Popover>
                                            );
                                        }
                                        break;
                                    case 'TypesFilterBlock':
                                        if (typesFilter.options.length > 1) {
                                            return (
                                                <Popover
                                                    key={i}
                                                    label={`Property Types${typesFilter.value?.length > 0 ? ` (${typesFilter.value.length})` : ''}`}
                                                    triggerClassName='button-primary-filled type-style-eyebrow'>
                                                    <MultiSelectCloud
                                                        name='types'
                                                        options={typesFilter.options}
                                                        toggleValue={typesFilter.toggleValue}
                                                        value={typesFilter.value} />
                                                </Popover>
                                            );
                                        }
                                        break;
                                }

                                return null;
                            })}
                        </div>
                    }

                    {/* Filter dialog */}
                    <Button
                        className='button-primary-filled type-style-eyebrow !gap-5'
                        onClick={() => setFiltersOpen(true)}>
                        <IconTune />
                        <span className='hidden sm:inline'>
                            Filters
                        </span>
                    </Button>
                    <Dialog
                        className='filter-dialog theme-light'
                        onOpenChange={(open) => setFiltersOpen(open)}
                        open={filtersOpen}>
                        <header className='flex justify-between p-16 border-b border-main-border h-[59px]'>
                            <Title>
                                Filters
                            </Title>
                            <Description className='sr-only'>
                                Apply filters to the listing
                            </Description>
                            <Close>
                                <IconClose />
                            </Close>
                        </header>

                        <form className='px-16'>
                            <div className='[&:not(:first-child)]:border-t border-main-border py-16'>
                                <fieldset className='flex w-full justify-between gap-16 h-[44px] items-center'>
                                    <legend className='sr-only'>Sort by</legend>
                                    <div
                                        aria-hidden={true}
                                        className='type-style-eyebrow'>
                                        Sort by
                                    </div>
                                    <div className='flex gap-8'>
                                        <PropertySortInput
                                            allowDistance={Boolean(mapBoundsFilter.value?.search)}
                                            onChange={setSortBy}
                                            value={sortBy} />
                                    </div>
                                </fieldset>
                            </div>

                            <div className='[&:not(:first-child)]:border-t border-main-border py-16'>
                                <fieldset className='flex w-full justify-between gap-16 h-[44px] items-center'>
                                    <legend className='sr-only'>Units</legend>
                                    <div
                                        aria-hidden={true}
                                        className='type-style-eyebrow'>
                                        Units
                                    </div>
                                    <div className='flex gap-8'>
                                        <AreaUnitsSelector />
                                        <CurrencySelector pageTitle={pageTitle} />
                                    </div>
                                </fieldset>
                            </div>

                            {dialogFilterSelection?.map(({ __typename: filterType, ...filter }, i) => {
                                switch (filterType) {
                                    case 'AmenityFilterBlock':
                                        if (filter.amenity) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <Checkbox
                                                        checked={amenitiesFilter.value?.includes(filter.amenity?.value)}
                                                        label={filter.amenity?.label}
                                                        name={`amenity-${filter.amenity?.value}`}
                                                        onCheckedChange={() => amenitiesFilter.toggleValue(filter.amenity?.value)} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'AmenitiesFilterBlock':
                                        if (amenitiesFilter.options.length > 1) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <MultiSelectCloud
                                                        label='Amenities'
                                                        name='amenities'
                                                        options={amenitiesFilter.options}
                                                        toggleValue={amenitiesFilter.toggleValue}
                                                        value={amenitiesFilter.value} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'BathroomsFilterBlock':
                                        if (bathroomsFilter.options.length > 2) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <Select
                                                        label='Bathrooms'
                                                        name='bathrooms'
                                                        onChange={(v) => bathroomsFilter.setValue(v)}
                                                        options={bathroomsFilter.options}
                                                        placeholder='Bathrooms'
                                                        value={bathroomsFilter.value} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'BedroomsFilterBlock':
                                        if (bedroomsFilter.options.length > 2) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <Select
                                                        label='Bedrooms'
                                                        name='bedrooms'
                                                        onChange={(v) => bedroomsFilter.setValue(v)}
                                                        options={bedroomsFilter.options}
                                                        placeholder='Bedrooms'
                                                        value={bedroomsFilter.value} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'OutdoorFilterBlock':
                                        return (
                                            <div
                                                className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                key={i}>
                                                <Checkbox
                                                    checked={outdoorFilter.value}
                                                    label='Outdoor space'
                                                    name='outdoor'
                                                    onCheckedChange={outdoorFilter.setValue} />
                                            </div>
                                        );
                                    case 'PriceFilterBlock':
                                        if (priceFilter.range[1] > priceFilter.range[0]) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <PriceSlider
                                                        label='Price range'
                                                        max={priceFilter.range[1]}
                                                        min={priceFilter.range[0]}
                                                        name='price'
                                                        onChange={(value) => priceFilter.setValue(value)}
                                                        value={priceFilter.value} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'SizeFilterBlock':
                                        if (sizeFilter.range[1] > sizeFilter.range[0]) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <AreaSlider
                                                        label='Size'
                                                        max={sizeFilter.range[1]}
                                                        min={sizeFilter.range[0]}
                                                        onChange={sizeFilter.setValue}
                                                        value={sizeFilter.value} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'TenuresFilterBlock':
                                        if (tenuresFilter.options.length > 1) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <MultiSelectCloud
                                                        label='Tenancy'
                                                        name='tenancy'
                                                        options={tenuresFilter.options}
                                                        toggleValue={tenuresFilter.toggleValue}
                                                        value={tenuresFilter.value} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'TypesFilterBlock':
                                        if (typesFilter.options.length > 1) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <MultiSelectCloud
                                                        label='Property types'
                                                        name='types'
                                                        options={typesFilter.options}
                                                        toggleValue={typesFilter.toggleValue}
                                                        value={typesFilter.value} />
                                                </div>
                                            );
                                        }
                                        break;
                                    case 'NewHomeFilterBlock':
                                        if (newHomeFilter) {
                                            return (
                                                <div
                                                    className='[&:not(:first-child)]:border-t border-main-border py-16'
                                                    key={i}>
                                                    <Checkbox
                                                        checked={newHomeFilter.value}
                                                        disabled={newHomeFilter.disabled}
                                                        label='New homes'
                                                        name='newHome'
                                                        onCheckedChange={newHomeFilter.setValue} />
                                                </div>
                                            );
                                        }
                                        break;
                                }

                                return null;
                            })}
                        </form>

                        <footer className='sticky bottom-0 flex justify-between px-16 py-12 gap-8'>
                            <Button
                                className='button-brand-filled type-style-eyebrow w-full'
                                onClick={() => setFiltersOpen(false)}>
                                Apply filters
                            </Button>
                            <Button
                                className='button-secondary-filled type-style-eyebrow'
                                onClick={clearFilters}>
                                Clear
                            </Button>
                        </footer>
                    </Dialog>
                </div>

                {/* Clear */}
                <Button
                    className='theme-light hidden md:block type-style-eyebrow text-main-text-secondary underline underline-offset-4 decoration-main-border'
                    onClick={() => {
                        clearFilters();
                        areasFilter.clear();
                        mapBoundsFilter.clear();
                    }}>
                    Clear all
                </Button>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default PropertyListingFilters;
